/**
 * THE GO-LIVE -> IN-SERVICE HANDOFF — #435 surface 2, the derivation.
 *
 * One pure place for the judgements the handoff UI reads, so the prompt, the board
 * header and the portfolio can never disagree about whether a board has gone live,
 * is ready to, or has sailed past its go-live untransitioned. Nothing here writes;
 * every input is passed in, so each function is a plain, testable rule — the same
 * shape as src/lifecycle.ts, which this sits beside.
 *
 * THE MODEL (from the approved mockups):
 *  - A board is IN SERVICE once it has been explicitly handed off — `actualGoLive`
 *    is set. That frozen moment, not a derived guess, is the gate; before the
 *    handoff a project stays "in delivery" however finished its phases look.
 *  - The handoff PROMPT surfaces when the project looks ready: the planned go-live
 *    has arrived and every delivery phase is done ('ready'), or one of those two
 *    signals is present but not both ('provisional' — the honest variant, which
 *    informs and never blocks). The snapshot it freezes is marked provisional then.
 *  - A project past its planned go-live but not yet handed off is OVERDUE — a quiet
 *    amber nudge at first, red once it has been too long. This is the pull half of
 *    adoption: an untransitioned board reads visibly wrong in the portfolio.
 */

import type { PhaseStatus } from './phases';
import type { DeliverySnapshot } from './types';

/** Days past the planned go-live at which the overdue nudge turns from amber to
 *  red (decision 4). A fortnight: long enough not to nag on day one, short enough
 *  that a shipped-but-unmarked project does not sit mislabelled for a month. */
export const GO_LIVE_OVERDUE_RED_DAYS = 14;

/** How many service cards must finish after go-live before a just-live board drops
 *  its calm "Settling in" state for a confident flow read (decision 2). Mirrors the
 *  forecast's FORECAST_MIN_FINISHED (insights.ts) — the same "not enough history for
 *  an honest read yet" bar, so the two never disagree about when data is enough. */
export const SETTLING_MIN_FINISHED = 8;

const DAY_MS = 24 * 60 * 60 * 1000;

/** A ms moment as the START of its plain UTC calendar day (ms). The go-live dates
 *  the Plan and portfolio show are read as their UTC day (new Date(ms).toISOString),
 *  so counting days from here matches the dates a person actually sees instead of
 *  letting a few hours of timezone offset silently drop a whole day. */
function utcDayStart(ms: number): number {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** Whole calendar days from day(a) to day(b), each read as its plain UTC day. The
 *  ONE schedule-day count every surface shares — the overdue nudge, the handoff
 *  variance badge and the frozen as-delivered snapshot — so they can never disagree
 *  by a day (the ribbon once read "20 days" while the dialog froze "19", because a
 *  local-midnight planned date was differenced against a UTC-midnight actual with a
 *  truncating ms subtraction). Positive = b is later. */
export function wholeDaysBetween(a: number, b: number): number {
  return Math.round((utcDayStart(b) - utcDayStart(a)) / DAY_MS);
}

/** Has this board been handed off — is it explicitly in service? The one gate the
 *  board header and the portfolio read; `actualGoLive` is frozen at the handoff and
 *  survives a revert, so this is deliberately the stored moment, never re-derived. */
export function isInService(board: { actualGoLive?: number | null }): boolean {
  return board.actualGoLive != null;
}

/** Are all the board's delivery phases finished? Mirrors the old boardAtService
 *  phase rule (EpicsLens): at least one phase, and every one done. A board with no
 *  phases has nothing to call finished, so it is not "all done". */
export function allDeliveryPhasesDone(phases: { status: PhaseStatus }[]): boolean {
  return phases.length > 0 && phases.every((p) => p.status === 'done');
}

/** Has the planned go-live arrived? A project with no planned date has no date gate
 *  (false), so readiness then rests on the phases alone. */
export function goLiveReached(plannedGoLive: number | undefined | null, nowMs: number): boolean {
  return typeof plannedGoLive === 'number' ? plannedGoLive <= nowMs : false;
}

export type HandoffReadiness =
  | { state: 'inservice' }
  | { state: 'ready' }
  | { state: 'provisional'; reason: 'phase-open' | 'before-date' }
  | { state: 'none' };

/**
 * WHERE A BOARD STANDS ON THE HANDOFF, from the state it already carries.
 *
 *  - Not a project (continuous), or no project plan at all → 'none' (a service
 *    board was never a project to hand off).
 *  - Already handed off (`actualGoLive` set) → 'inservice'.
 *  - Planned go-live reached AND every delivery phase done → 'ready' (the firm
 *    prompt; the snapshot is final).
 *  - Exactly one of those signals → 'provisional': 'phase-open' when the date has
 *    arrived but a phase is still running (Hypercare is legitimate — inform, never
 *    block), 'before-date' when the work is all done ahead of the planned date (go
 *    live early if you like).
 *  - Neither → 'none' (nothing to prompt yet).
 */
export function handoffReadiness(input: {
  deliveryMode?: 'project' | 'continuous' | null;
  tracked: boolean;
  actualGoLive?: number | null;
  plannedGoLive?: number | null;
  phases: { status: PhaseStatus }[];
  nowMs: number;
}): HandoffReadiness {
  if (input.deliveryMode === 'continuous' || !input.tracked) return { state: 'none' };
  if (input.actualGoLive != null) return { state: 'inservice' };
  const reached = goLiveReached(input.plannedGoLive, input.nowMs);
  const allDone = allDeliveryPhasesDone(input.phases);
  // A project tracked by a go-live but with NO delivery phases has nothing left to
  // finish: once its date arrives it is READY, not "a phase is still running". Only a
  // board that actually has an unfinished phase is the phase-open provisional case —
  // otherwise a phaseless project (a common shape, tracked by status columns alone)
  // could never reach 'ready' and was always wrongly told work was unfinished.
  const nothingUnfinished = allDone || input.phases.length === 0;
  if (reached && nothingUnfinished) return { state: 'ready' };
  if (reached) return { state: 'provisional', reason: 'phase-open' };
  if (allDone) return { state: 'provisional', reason: 'before-date' };
  return { state: 'none' };
}

/** Whether the handoff should be offered at all right now (ready or provisional). */
export function handoffOffered(r: HandoffReadiness): boolean {
  return r.state === 'ready' || r.state === 'provisional';
}

/**
 * HOW OVERDUE a project is for its handoff. `null` when it is not overdue (not a
 * project, already live, or the planned go-live has not arrived). Otherwise the
 * days past the plan and whether that has crossed into the red band. Drives the
 * portfolio's "Go-live passed — mark it live" nudge (amber, then red).
 */
export function goLiveOverdue(input: {
  deliveryMode?: 'project' | 'continuous' | null;
  tracked: boolean;
  actualGoLive?: number | null;
  plannedGoLive?: number | null;
  nowMs: number;
}): { days: number; level: 'amber' | 'red' } | null {
  if (input.deliveryMode === 'continuous' || !input.tracked) return null;
  if (input.actualGoLive != null) return null;
  if (!goLiveReached(input.plannedGoLive, input.nowMs)) return null;
  const days = wholeDaysBetween(input.plannedGoLive as number, input.nowMs);
  return { days, level: days >= GO_LIVE_OVERDUE_RED_DAYS ? 'red' : 'amber' };
}

/**
 * IS A JUST-LIVE BOARD STILL SETTLING IN? A board reads a calm "Settling in" state
 * until enough service work has finished to read its flow honestly (decision 2) —
 * the same guard the forecast uses, rather than showing a confident RAG on no data.
 * Only meaningful once in service; false otherwise.
 */
export function settlingIn(input: {
  actualGoLive?: number | null;
  finishedSinceGoLive: number;
}): boolean {
  if (input.actualGoLive == null) return false;
  return input.finishedSinceGoLive < SETTLING_MIN_FINISHED;
}

/**
 * THE AS-DELIVERED SNAPSHOT, frozen at the handoff. Pure: the caller counts the
 * delivery cards (shipped = resolved done; deferred = cut or still open and carried
 * into service) and reads the budget; this assembles the record and works out the
 * schedule variance (actual minus planned go-live, in days). `provisional` is set
 * when the handoff was confirmed before it was fully ready (a phase still open, or
 * ahead of the planned date), so a reader knows the figures may still move.
 */
export function buildDeliverySnapshot(input: {
  plannedGoLive?: number | null;
  actualGoLive: number;
  scopeShipped: number;
  scopeDeferred: number;
  budgetAtGoLive?: number | null;
  provisional: boolean;
  nowMs: number;
}): DeliverySnapshot {
  const snap: DeliverySnapshot = {
    actualGoLive: input.actualGoLive,
    scopeShipped: input.scopeShipped,
    scopeDeferred: input.scopeDeferred,
    scopeTotal: input.scopeShipped + input.scopeDeferred,
    at: input.nowMs,
  };
  if (typeof input.plannedGoLive === 'number') {
    snap.plannedGoLive = input.plannedGoLive;
    snap.scheduleVarianceDays = wholeDaysBetween(input.plannedGoLive, input.actualGoLive);
  }
  if (typeof input.budgetAtGoLive === 'number') snap.budgetAtGoLive = input.budgetAtGoLive;
  if (input.provisional) snap.provisional = true;
  return snap;
}
